import { useToast } from '@/_shared/hooks/toast.js';
import { Button, Divider, TableCell, TableRow, Tooltip, mergeClasses } from '@fluentui/react-components';
import { CopyRegular } from '@fluentui/react-icons';
import { trackException } from '@this/app-shared/dist/tools/insights/track.js';
import { Fragment, type MouseEvent, useCallback } from 'react';
import { useSharedStyles as useButtonContainerStyles } from '../../shared-styles.js';
import { useSharedStyles } from '../shared-styles.js';
import type { FieldTableProps, FieldValue, FieldValueRecord } from '../types.js';
import { useStyles } from './styles.js';

type FieldTableRowProps<TValue extends Array<FieldValueRecord>> = {
    value: TValue[number];
    tableCells: FieldTableProps<TValue>['tableCells'];
    cellsToRight: Record<keyof TValue[number], boolean>;
};

export function FieldTableRow<TValue extends Array<FieldValueRecord>>({
    tableCells,
    value,
    cellsToRight,
}: FieldTableRowProps<TValue>) {
    const styles = useStyles();
    const sharedStyles = useSharedStyles();
    const buttonContainerStyles = useButtonContainerStyles();

    const toast = useToast();

    const onValueClick = useCallback(
        (event: MouseEvent<HTMLSpanElement>, cell: FieldTableProps<TValue>['tableCells'][number]) => {
            cell.onValueClick?.(event, value[cell.key], value);
        },
        [value]
    );

    const onCopyClick = useCallback(
        (cellValue: unknown) => {
            if (typeof cellValue === 'string' || typeof cellValue === 'number') {
                navigator.clipboard
                    .writeText(cellValue.toString())
                    .then(() => {
                        toast('Wert kopiert', 'Der Wert wurde in die Zwischenablage kopiert', {
                            intent: 'success',
                        });
                    })
                    .catch((error) => {
                        trackException(error);
                        toast('Fehler beim Kopieren', 'Der Wert konnte nicht in die Zwischenablage kopiert werden', {
                            intent: 'error',
                        });
                    });
            }
        },
        [toast]
    );

    return (
        <Fragment>
            <TableRow className={sharedStyles.tableDisplayContents}>
                <TableCell className={sharedStyles.tableDisplayContents}>
                    <Divider className={sharedStyles.tableDivider} />
                </TableCell>
            </TableRow>

            <TableRow className={mergeClasses(sharedStyles.tableDisplayContents, styles.row)}>
                {tableCells.map((cell, cellIndex) => (
                    <TableCell
                        key={cellIndex}
                        className={mergeClasses(
                            sharedStyles.tableCell,
                            cellsToRight[cell.key] && sharedStyles.tableCellAlignRight
                        )}
                    >
                        <div className={styles.tableCellContent}>
                            <Tooltip
                                visible={!cell.tooltip ? false : undefined}
                                content={cell.tooltip ?? ''}
                                relationship="label"
                                positioning="below-start"
                            >
                                <span
                                    className={mergeClasses(
                                        styles.tableCellContent,
                                        (cell.onValueClick !== undefined || cell.valueLinkURL !== undefined) &&
                                            styles.tableCellAction
                                    )}
                                    onClick={(event) => onValueClick(event, cell)}
                                >
                                    {cell.valueLinkURL ? (
                                        <a href={cell.valueLinkURL}>
                                            {cell.serializer?.(value) ??
                                                (typeof value[cell.key] === 'object'
                                                    ? null
                                                    : (value[cell.key] as FieldValue))}
                                        </a>
                                    ) : (
                                        <span>
                                            {cell.serializer?.(value) ??
                                                (typeof value[cell.key] === 'object'
                                                    ? null
                                                    : (value[cell.key] as FieldValue))}
                                        </span>
                                    )}
                                </span>
                            </Tooltip>

                            {cell.copyable && (
                                <div className={buttonContainerStyles.container}>
                                    <Button
                                        data-copy-field-value
                                        className={buttonContainerStyles.button}
                                        appearance="subtle"
                                        size="small"
                                        icon={<CopyRegular />}
                                        onClick={onCopyClick.bind(null, value[cell.key])}
                                    />
                                </div>
                            )}
                        </div>
                    </TableCell>
                ))}
            </TableRow>
        </Fragment>
    );
}
